import { Deadline } from './deadline';
import { Refusal } from './refusal';

/**
 * What the browser was handed when it came back from the provider
 */
export interface Returned {
	/** An access token, from a project still using the implicit return */
	token?: string;
	/** An authorization code, which is exchanged with the verifier this browser kept */
	code?: string;
	/** `recovery`, `signup`, `magiclink` — what the person was doing when they left */
	kind?: string;
	error?: string;
}

/**
 * The browser's side of the identity provider handshake.
 *
 * The provider round trip of GitHub and Google: where to send the browser, and exchanging the code it
 * comes back with for the provider's access token, which reaches Accounts once. Passwords never pass
 * through here: they go to the service (`Credentials`), which applies the policy and the throttle. Every
 * decision about whether a token means anything is taken by the service, which verifies the signature
 * itself.
 *
 * The return is an **authorization code**, not a token in the address fragment. The two differ in
 * one way that matters: a token in a fragment is a credential that has already been handed over, and
 * it is in the address bar, in the browser's history and, if anything ever links onward, in a
 * referrer. A code is worthless without the verifier, which this browser invented, kept in
 * `sessionStorage` for the length of the trip, and never sent anywhere. The implicit return is still
 * read, because a project can be configured for it, and is reported as the weaker form it is.
 *
 * Nothing here is written to storage that outlives the tab, and the verifier is removed the moment
 * it has been spent.
 *
 * Every call ends within `wait`, its body included. A provider that does not answer, or answers with a
 * server error or a rate limit, is `UPSTREAM_UNAVAILABLE` — never "those details did not sign you in"
 * or "that link is no longer valid", which would send a person to change a password that was right or
 * to spend a recovery link that was still good.
 */
export class Provider {
	/** Where the verifier waits while the person is at the provider. One tab, one trip. */
	static VERIFIER = 'beyond.accounts.verifier';
	/** How long one call to the provider may take (ms), read when a provider is created */
	static WAIT = 20000;

	/**
	 * What the provider left on *this* page load, read once.
	 *
	 * Reading it is destructive — the value is taken out of the address so it cannot sit in history
	 * or leak through a referrer — so a second caller would find nothing and conclude the link was
	 * spent. That is not hypothetical: React invokes an effect twice in development, and the reset
	 * screen reported a perfectly good recovery link as expired because its own effect had already
	 * consumed it. One page load has one answer, and this is it.
	 */
	static #captured?: Returned;

	#url?: string;
	#key?: string;
	#fetcher: typeof fetch;
	#storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
	#wait: number;

	/**
	 * @param fetcher Bound to the global: a browser refuses `fetch` called as a method of anything else
	 * @param wait Milliseconds one call may take, its body included
	 */
	constructor(
		url: string | undefined,
		key: string | undefined,
		fetcher: typeof fetch = (...args) => globalThis.fetch(...args),
		storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined = globalThis.sessionStorage,
		wait = Provider.WAIT
	) {
		this.#url = url?.replace(/\/$/, '');
		this.#key = key;
		this.#fetcher = fetcher;
		this.#storage = storage;
		this.#wait = wait;
	}

	get configured(): boolean {
		return Boolean(this.#url && this.#key);
	}

	#headers(): Record<string, string> {
		return { apikey: this.#key as string, 'content-type': 'application/json', accept: 'application/json' };
	}

	/**
	 * One request to the provider and its body, within `wait`.
	 *
	 * @throws Refusal UPSTREAM_UNAVAILABLE when nothing came back in time, or a server error or a rate
	 * limit did — none of which says anything about the person's details
	 */
	async #send(path: string, init: RequestInit): Promise<{ response: Response; parsed?: Record<string, unknown> }> {
		const deadline = new Deadline(this.#wait);
		try {
			const response = await deadline.race(this.#fetcher(`${this.#url}/auth/v1${path}`, { ...init, signal: deadline.signal }));
			const parsed = (await deadline.race(response.json()).catch(() => undefined)) as Record<string, unknown> | undefined;
			if (deadline.passed || response.status >= 500 || response.status === 429) {
				throw new Refusal('UPSTREAM_UNAVAILABLE', 'The identity provider is not answering', response.status);
			}
			return { response, parsed };
		} catch (cause) {
			if (Refusal.is(cause)) throw cause;
			throw new Refusal('UPSTREAM_UNAVAILABLE', 'The identity provider could not be reached', 0, { cause: String(cause), timeout: deadline.passed });
		} finally {
			deadline.clear();
		}
	}

	async #call(path: string, body: unknown, method = 'POST'): Promise<Record<string, unknown>> {
		if (!this.configured) throw new Refusal('PROVIDER_NOT_ENABLED', 'No identity provider is configured');

		const { response, parsed } = await this.#send(path, { method, headers: this.#headers(), body: JSON.stringify(body) });
		// The provider's own wording is not shown: it distinguishes "no such user" from "wrong
		// password", which would let anyone test whether an address is registered here
		if (!response.ok) throw new Refusal('UNAUTHENTICATED', 'Those details did not sign you in', response.status);
		return parsed ?? {};
	}

	async #token(path: string, body: unknown): Promise<string> {
		const parsed = await this.#call(path, body);
		if (typeof parsed.access_token !== 'string' || !parsed.access_token) {
			throw new Refusal('UNAUTHENTICATED', 'Those details did not sign you in');
		}
		return parsed.access_token;
	}

	/**
	 * Where to send the browser for a provider handshake.
	 *
	 * The verifier is invented here and only its digest travels, so the code the provider returns is
	 * useless to anyone who did not start this trip in this tab.
	 */
	async authorize(provider: string, back: string): Promise<string> {
		const challenge = await this.#challenge();
		const address = new URL(`${this.#url}/auth/v1/authorize`);
		address.searchParams.set('provider', provider);
		address.searchParams.set('redirect_to', back);
		if (challenge) {
			address.searchParams.set('code_challenge', challenge);
			address.searchParams.set('code_challenge_method', 's256');
		}
		return address.toString();
	}

	/** Invents a verifier, keeps it for the trip and returns its digest */
	async #challenge(): Promise<string | undefined> {
		const crypto = globalThis.crypto;
		if (!crypto?.subtle || !this.#storage) return undefined;

		const bytes = crypto.getRandomValues(new Uint8Array(32));
		const verifier = Provider.#base64url(bytes);
		try {
			this.#storage.setItem(Provider.VERIFIER, verifier);
		} catch {
			// Without storage the trip cannot be proved, so the weaker return is used instead
			return undefined;
		}
		const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
		return Provider.#base64url(new Uint8Array(digest));
	}

	static #base64url(bytes: Uint8Array): string {
		let binary = '';
		for (const byte of bytes) binary += String.fromCharCode(byte);
		return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
	}

	/**
	 * Exchanges an authorization code for the provider's access token, using the verifier this tab
	 * kept. The verifier is spent once and removed whatever the outcome.
	 */
	async exchange(code: string): Promise<string> {
		let verifier: string | null | undefined;
		try {
			verifier = this.#storage?.getItem(Provider.VERIFIER);
			this.#storage?.removeItem(Provider.VERIFIER);
		} catch {
			verifier = undefined;
		}
		if (!verifier) throw new Refusal('UNAUTHENTICATED', 'That sign-in could not be completed. Start again.');
		return this.#token('/token?grant_type=pkce', { auth_code: code, code_verifier: verifier });
	}

	/**
	 * Reads what a provider left on the way back, and removes it from the address before anything
	 * else runs, so it cannot sit in browser history or leak through a referrer.
	 */
	static returned(location: Location = globalThis.location): Returned {
		if (Provider.#captured) return Provider.#captured;

		const fragment = new URLSearchParams(location.hash.replace(/^#/, ''));
		const search = new URLSearchParams(location.search);

		const token = fragment.get('access_token') ?? undefined;
		const code = search.get('code') ?? undefined;
		const kind = fragment.get('type') ?? search.get('type') ?? undefined;
		const error = fragment.get('error_description') ?? search.get('error_description') ?? undefined;

		if (token || code || error) {
			const clean = new URL(location.href);
			clean.hash = '';
			for (const name of ['code', 'error', 'error_description', 'state', 'type']) clean.searchParams.delete(name);
			globalThis.history?.replaceState(null, '', clean.toString());
		}
		Provider.#captured = { token, code, kind, error };
		return Provider.#captured;
	}

	/**
	 * Whether this page load came back from a provider with a code or a token to sign in with, read without
	 * touching the address, so a first render can wait for it under the opening before `returned()` runs
	 */
	static carrying(location: Location = globalThis.location): boolean {
		if (Provider.#captured) return Boolean(Provider.#captured.token || Provider.#captured.code);
		const fragment = new URLSearchParams(location.hash.replace(/^#/, ''));
		return Boolean(fragment.get('access_token') || new URLSearchParams(location.search).get('code'));
	}

	/** Forgets what this page load carried, for a test that drives several in one process */
	static forget(): void {
		Provider.#captured = undefined;
	}
}
